import React from "react";
import {
  FileText,
  ShieldCheck,
  Lock,
  Database,
  Users,
} from "lucide-react";
import Footer from "../../../site/components/Footer";
import { useTranslation } from 'react-i18next';


/* ------------------ SMALL HELPERS ------------------ */

const Highlight = ({ children }) => (
  <span className="font-semibold text-purple-700">{children}</span>
);

const Divider = () => (
  <div className="my-16 h-[1px] w-full bg-gradient-to-r from-transparent via-purple-200 to-transparent" />
);

const Section = ({ title, icon: Icon, children }) => (
  <section>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-purple-600" />
      </div>

      <h2
        className="
          text-2xl md:text-3xl font-semibold
          bg-gradient-to-r from-purple-700 via-purple-500 to-indigo-600
          bg-clip-text text-transparent
        "
      >
        {title}
      </h2>
    </div>

    <div className="pl-[3.25rem] text-gray-700 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

/* ------------------ PAGE ------------------ */

const TermsConditions = () => {
  return (
    <>

      <main className="relative bg-white overflow-hidden">
        {/* SOFT BACKGROUND */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50 via-white to-white pointer-events-none" />

        <div className="relative container mx-auto px-6 py-24 max-w-7xl mt-6">
          {/* PAGE HERO */}
          <header className="mb-16">
            <p
              className="
                inline-block
                text-sm uppercase tracking-widest font-semibold
                text-purple-700
                px-4 py-1.5 rounded-full
                bg-gradient-to-r
                from-purple-100 via-purple-50 to-indigo-100
                mb-4
              "
            >
              Legal & Compliance
            </p>

            <h1
              className="
                text-4xl md:text-5xl font-semibold leading-tight
                bg-gradient-to-r
                from-pink-600 via-purple-600 to-indigo-600
                bg-clip-text text-transparent pb-2
              "
            >
              Terms & Conditions
            </h1>

            <p className="mt-6 text-gray-600 max-w-4xl text-lg">
              These <Highlight>Terms & Conditions</Highlight> govern your access
              to and use of Essaar software, platforms, and services. By using
              Essaar, you agree to comply with these terms.
            </p>
          </header>

          {/* ABOUT */}
          <Section title="About Essaar" icon={ShieldCheck}>
            <p>
              <Highlight>Essaar</Highlight> is a Software-as-a-Service (SaaS)
              platform offering:
            </p>

            <ul className="list-disc pl-6 space-y-1">
              <li>Logistics & Shipment Booking Software</li>
              <li>Inventory Management Software</li>
              <li>Attendance & Workforce Management Software</li>
              <li>Digital Marketing & Website Development Services</li>
              <li>Automation, Webhooks, WhatsApp & GPS integrations</li>
            </ul>

            <p>
              Services are provided on a{" "}
              <Highlight>subscription or usage basis</Highlight> unless otherwise
              agreed.
            </p>
          </Section>

          <Divider />

          {/* ELIGIBILITY */}
          <Section title="Eligibility & Account Registration" icon={Users}>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must be 18 years or older to use Essaar</li>
              <li>You are responsible for your login credentials</li>
              <li>You must provide accurate and complete information</li>
              <li>All activity under your account is your responsibility</li>
            </ul>

            <p>
              Essaar may suspend or terminate accounts for{" "}
              <Highlight>false information or misuse</Highlight>.
            </p>
          </Section>

          <Divider />

          {/* PAYMENTS */}
          <Section title="Subscription, Pricing & Payments" icon={Database}>
            <ul className="list-disc pl-6 space-y-1">
              <li>Some services require a paid subscription</li>
              <li>Pricing and plans may change with prior notice</li>
              <li>Payments are non-refundable unless stated otherwise</li>
              <li>Failure to pay may result in service suspension</li>
              <li>GST and statutory taxes apply as per law</li>
            </ul>
          </Section>

          <Divider />

          {/* USAGE */}
          <Section title="Use of Services" icon={FileText}>
            <p>You agree to use Essaar only for lawful business purposes.</p>

            <ul className="list-disc pl-6 space-y-1">
              <li>No illegal, fraudulent, or harmful activities</li>
              <li>No unauthorized access attempts</li>
              <li>No reverse engineering or resale</li>
              <li>No malware or abusive content</li>
            </ul>

            <p>
              Violations may result in{" "}
              <Highlight>immediate account termination</Highlight>.
            </p>
          </Section>

          <Divider />

          {/* SECURITY */}
          <Section title="Data Ownership & Security" icon={Lock}>
            <p>
              All customer data remains{" "}
              <Highlight>your property</Highlight>.
            </p>

            <p>
              Essaar implements industry-standard security but cannot guarantee
              absolute protection against all risks.
            </p>
          </Section>

          <Divider />

          {/* LIABILITY */}
          <Section title="Limitation of Liability" icon={ShieldCheck}>
            <p>
              Essaar is not liable for indirect or consequential damages, data
              loss due to user error, or third-party service failures.
            </p>

            <p>
              Use of the platform is at your{" "}
              <Highlight>own risk</Highlight>.
            </p>
          </Section>

          <Divider />

          {/* LAW */}
          <Section title="Governing Law & Jurisdiction" icon={FileText}>
            <p>
              These Terms are governed by the laws of{" "}
              <Highlight>India</Highlight>.
            </p>

            <p>
              Disputes are subject to courts located in{" "}
              <Highlight>Mumbai</Highlight>.
            </p>
          </Section>

          <Divider />

          {/* CONTACT */}
          <Section title="Contact Information" icon={Users}>
            <p>
              📧 Email: <Highlight>support@digiqin.com</Highlight>
            </p>
            <p>
              📞 Phone: <Highlight>+91 98925 75520</Highlight>
            </p>
            <p>
              🌐 Website: <Highlight>https://www.digiqin.com</Highlight>
            </p>
          </Section>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </>
  );
};

export default TermsConditions;
